import React from 'react';
import { cn } from '../../design-system/cn';
import { typography, semanticColors, spacing } from '../../design-system/tokens';

const TONES = {
  brand: 'text-emerald-400/95',
  cyan: 'text-cyan-400/90',
  muted: semanticColors.text.muted,
  amber: 'text-amber-400/90',
};

/**
 * Canonical home-shell section header. Consolidates the bespoke eyebrow/label
 * markup used across Board, My Arena, Live Arena, Arena Social, and Upcoming so
 * every section shares one heading hierarchy.
 *
 * Modes:
 * - default: optional emoji icon + uppercase eyebrow + optional description,
 *   with an optional right-aligned `action` node.
 * - `divider`: eyebrow followed by a trailing hairline rule (Start-a-Lock style).
 */
export default function SectionHeading({
  eyebrow,
  description,
  descriptionClassName,
  icon,
  action,
  tone = 'brand',
  divider = false,
  align = 'start',
  className,
}) {
  const toneClass = TONES[tone] || TONES.brand;
  const centered = align === 'center';

  if (divider) {
    return (
      <div className={cn('flex items-center', spacing.stack.lg, spacing.label.belowMd, className)}>
        <p className={cn(typography.eyebrow, toneClass, 'shrink-0')}>{eyebrow}</p>
        <div className={cn('flex-1 h-px', semanticColors.surface.divider)} />
        {action && <div className="shrink-0">{action}</div>}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-start justify-between gap-2',
        centered && 'md:justify-center md:text-center',
        className,
      )}
    >
      <div
        className={cn(
          'min-w-0 flex items-start gap-2',
          centered && 'md:flex-col md:items-center',
        )}
      >
        {icon && <span className="text-sm md:text-base shrink-0" aria-hidden>{icon}</span>}
        <div className="min-w-0">
          <p className={cn(typography.eyebrow, toneClass)}>{eyebrow}</p>
          {description && (
            <p className={cn('text-xs font-medium mt-0.5', semanticColors.text.muted, descriptionClassName)}>{description}</p>
          )}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
